import { MimeType } from 'vet-bones/constants';
import { alphaSort } from 'vet-bones/utils';

import { databasesStardogRequestDispatchers } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { PublishProjectResponse } from 'src/actions/projects/actionCreators';
import { Database } from 'src/store/state/databases';
import * as copy from 'src/templates/copy';
import { PublishFileUpload } from 'src/utils/export/types';
import { stringifyProjectMetadata } from 'src/utils/stringify/projectMetadata';

const fileSorter = alphaSort(
  ({ mappingInfo }: PublishFileUpload) => mappingInfo.namedGraphIri
);

export const publishFileUploadsMetadata = async ({
  database,
  exports,
  publishedDate,
}: {
  database: Database;
  exports: PublishFileUpload[];
  publishedDate: number;
}): Promise<PublishProjectResponse> => {
  // Begin a transaction
  const transactionResponse =
    await databasesStardogRequestDispatchers.beginDatabaseTransaction(
      database.id
    );

  const transactionId = transactionResponse.payload.response?.body;

  if (
    transactionResponse.type ===
      DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
    !transactionId
  ) {
    return {
      success: false,
      action: transactionResponse,
      actionDescription: copy.notifications.database.beginTransaction,
      message: copy.notifications.publishProject.fileUploadMetadata.failure,
    };
  }

  const namespaces = database.namespaces || {};

  const metadata = exports
    .sort(fileSorter)
    .map(({ mappingInfo, resourceName }) =>
      stringifyProjectMetadata(
        mappingInfo.namedGraphIri,
        resourceName,
        publishedDate,
        namespaces
      )
    )
    .join('\n\n');

  // add the data
  const addDataResponse =
    await databasesStardogRequestDispatchers.addDataToDatabase({
      databaseId: database.id,
      transactionId,
      contents: metadata,
      mimeType: MimeType.TRIG,
    });

  if (
    addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
  ) {
    // Stardog doesn't actually start a transaction until an action
    // has succeeded, so no need to rollback the first one if it fails
    return {
      success: false,
      action: addDataResponse,
      actionDescription: copy.notifications.database.addData,
      message: copy.notifications.publishProject.fileUploadMetadata.failure,
    };
  }

  // Commit the transaction
  const commitResponse =
    await databasesStardogRequestDispatchers.commitDatabaseTransaction(
      database.id,
      transactionId
    );

  if (
    commitResponse.type ===
    DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
  ) {
    await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
      database.id,
      transactionId
    );
    return {
      success: false,
      action: commitResponse,
      actionDescription: copy.notifications.database.commitTransaction,
      message: copy.notifications.publishProject.fileUploadMetadata.failure,
    };
  }

  return {
    success: true,
    action: commitResponse,
    message: copy.notifications.publishProject.fileUploadMetadata.success,
  };
};
